import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoCtx } from './DemoContext';
import { IrrForm } from './panels/IrrForm';
import { SiteRegForm } from './panels/SiteRegForm';
import { SITES, STATUS_LABEL, STATUS_COLOR, type Status } from './demoData';
import DemoMap from './DemoMap';

const KPI = [
    { lbl: '총 생육단지', val: '8', sub: '전월 대비 +1' },
    { lbl: '총 관리 면적', val: '21,840', sub: '㎡ (6,607평)' },
    { lbl: '수분 양호율', val: '62.5%', sub: '단지 5/8개 양호' },
    {
        lbl: '오늘 관수 필요',
        val: '3',
        sub: '단지 긴급 대응 필요',
        danger: true,
    },
    { lbl: '평균 토양 수분율', val: '55.3%', sub: '전체 단지 평균' },
    { lbl: '주간 누적 강수량', val: '12.4mm', sub: '최근 7일 기준' },
];

const ALERT_SITES = SITES.filter(s => s.status !== 'good').sort(
    (a, b) => a.moist - b.moist,
);

function SiteRegFooter() {
    const { toast, closePanel, savePendingSite } = useDemoCtx();
    return (
        <>
            <button
                className="btn btn-ghost"
                onClick={() => toast('임시저장 완료', 'success')}
            >
                임시저장
            </button>
            <button
                className="btn btn-secondary"
                onClick={() => toast('다시 그리기', 'success')}
            >
                다시 그리기
            </button>
            <button
                className="btn btn-primary"
                style={{ flex: 2 }}
                onClick={() => {
                    const site = savePendingSite();
                    const label = site?.form.name || '생육단지';
                    toast(`'${label}' 등록 완료 ✓`, 'success');
                    closePanel();
                }}
            >
                저장하기
            </button>
        </>
    );
}

export default function DemoDashboard() {
    const {
        openPanel,
        setPendingPolygon,
        savedSites,
        deleteSavedSite,
        drawingModeRequest,
        clearDrawingModeRequest,
    } = useDemoCtx();
    const navigate = useNavigate();
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        if (drawingModeRequest) {
            setIsDrawing(true);
            clearDrawingModeRequest();
        }
    }, [drawingModeRequest, clearDrawingModeRequest]);

    const openIrr = (name: string) =>
        openPanel({
            title: '관수 등록',
            body: <IrrForm siteName={name} />,
        });

    const handleDrawingComplete = (pts: { lat: number; lng: number }[]) => {
        setIsDrawing(false);
        setPendingPolygon(pts);
        openPanel({
            title: '생육단지 등록',
            body: <SiteRegForm />,
            footer: <SiteRegFooter />,
        });
    };

    return (
        <div className="dashboard-wrap">
            {/* KPI */}
            <div className="kpi-row">
                {KPI.map((k, i) => (
                    <div
                        key={i}
                        className="kpi-card fade-up"
                        style={{ animationDelay: `${i * 0.05}s` }}
                        onClick={() => navigate('/demo/sites')}
                    >
                        <div className="kpi-lbl">{k.lbl}</div>
                        <div
                            className="kpi-val"
                            style={k.danger ? { color: 'var(--danger)' } : {}}
                        >
                            {k.val}
                        </div>
                        <div className="kpi-sub">{k.sub}</div>
                    </div>
                ))}
            </div>

            {/* Map + Panel */}
            <div className="map-row" style={{ flex: 1, minHeight: 0 }}>
                <div className="map-wrap" style={{ position: 'relative' }}>
                    <DemoMap
                        drawingMode={isDrawing}
                        onDrawingComplete={handleDrawingComplete}
                        savedSites={savedSites}
                        onDeleteSite={deleteSavedSite}
                    />

                    {/* 수분 상태 범례 */}
                    <div className="map-legend" style={{ zIndex: 1000 }}>
                        <div className="legend-ttl">수분 상태</div>
                        {(
                            ['good', 'caution', 'warn', 'danger'] as Status[]
                        ).map(s => (
                            <div key={s} className="legend-row">
                                <div
                                    className="legend-sq"
                                    style={{ background: STATUS_COLOR[s] }}
                                />
                                {STATUS_LABEL[s]}
                            </div>
                        ))}
                    </div>

                    {/* 생육단지 등록 / 취소 버튼 */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 10,
                            right: 14,
                            zIndex: 1000,
                            display: 'flex',
                            gap: 8,
                        }}
                    >
                        {isDrawing ? (
                            <button
                                className="btn btn-sm"
                                style={{
                                    background: '#fff',
                                    color: '#EF4444',
                                    border: '1.5px solid #EF4444',
                                    fontWeight: 700,
                                }}
                                onClick={() => setIsDrawing(false)}
                            >
                                ✕ 취소
                            </button>
                        ) : (
                            <button
                                className="btn btn-info btn-sm"
                                onClick={() => setIsDrawing(true)}
                            >
                                ＋ 생육단지 등록
                            </button>
                        )}
                    </div>
                </div>

                {/* Alert Panel */}
                <div className="alert-pnl">
                    <div className="apnl-hd">
                        <span className="apnl-title">🚨 관수 필요 단지</span>
                        <span className="badge badge-danger">
                            {ALERT_SITES.length}건
                        </span>
                    </div>
                    <div className="alert-list">
                        {ALERT_SITES.map((s, i) => (
                            <div
                                key={i}
                                className="alert-item fade-up"
                                style={{ animationDelay: `${i * 0.05}s` }}
                                onClick={() =>
                                    navigate(
                                        `/demo/sites/${encodeURIComponent(s.name)}`,
                                    )
                                }
                            >
                                <div className="ai-top">
                                    <span className="ai-name">{s.name}</span>
                                    <span className={`badge badge-${s.status}`}>
                                        <span
                                            className={`badge-dot dot-${s.status}`}
                                        />
                                        {STATUS_LABEL[s.status]}
                                    </span>
                                </div>
                                <div className="ai-meta">
                                    <span>👤 {s.mgr}</span>
                                    <span>📅 {s.days}일전</span>
                                </div>
                                <div className="ai-stats">
                                    <div className="ai-stat">
                                        <div className="ai-stat-lbl">
                                            현재 수분율
                                        </div>
                                        <div
                                            className="ai-stat-val"
                                            style={{
                                                color: STATUS_COLOR[s.status],
                                            }}
                                        >
                                            {s.moist}%
                                        </div>
                                    </div>
                                    <div className="ai-stat">
                                        <div className="ai-stat-lbl">
                                            스트레스
                                        </div>
                                        <div className="ai-stat-val">
                                            {s.stress}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-primary btn-sm w-full"
                                    onClick={e => {
                                        e.stopPropagation();
                                        openIrr(s.name);
                                    }}
                                >
                                    💧 관수 등록
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
